import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, message, Tag, Typography, Modal, Tooltip } from 'antd';
import { getColumnSearchProps } from '../../../approvalsBonus/presentation/filterSearch.jsx';
import ApprovalRewardsTable from '../../presentation/approvalRewardsTable/ApprovalRewardsTable.jsx';
import uuid from 'uuid/v4';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { useSelector } from 'react-redux';
import { parse, fetchModernFormQuestions } from 'src/_shared/services/utils.js';
import { addCommas } from '../../../rewardsAndRecognitionHelper/utils.js';

const { Text, Paragraph } = Typography;

const QUERY_BADGE_ASSIGNMENTS = gql`
  query QueryBadgeAssignments($companyId: ID!, $nextToken: String) {
    queryBadgeAssignments(
      companyId: $companyId
      nextToken: $nextToken
    ) {
      items {
        approvalStatus
        awardedBy
        awardedByUser {
          id
          firstName
          lastName
          emailAddress
        }
        id
        badgeId
        badge {
            id
            name
			description
			points
            snappyGrantId
            snappyGrant {
				id
                name
                costInCurrency
				externalSnappyId
				snappyCampaignTierId
            }
        }
        rewardBonus
        companyId
        userGroupId
        justificationFormAnswers
        userId
        createdAt
        updatedAt
        completedAt
        user {
          id
          firstName
          lastName
          emailAddress
          recognitionEvents {
            currentBudget
          }
        }
		declineReason
      }
      nextToken
    }
  }
`;

const ApprovedContainer = (props) => {
	const { isTableRefresh, setTableRefresh } = props;

	const searchInput = useRef(null);
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.company?.id;

	const [approvalStatus, setApprovalStatus] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [nextToken, setNextToken] = useState(null);
	const [showJustificationModal, setShowJustificationModal] = useState(false);
	const [selectedJustification, setSelectedJustification] = useState([]);

	useEffect(() => {
		if (isTableRefresh) {
			setTableRefresh(false);
			getApprovalRewards();
		}
	}, [isTableRefresh]);

	const getJustificationContent = (formData, justificationData) => {
		const content = [];
		formData.elements.forEach(ele => {
			const answer = justificationData[ele.id]
			content.push({question: ele.label, answer})
		});
		return content;
	}

	const getApprovalRewards = async () => {
		if (!companyId) {
			message.error('Company ID not found');
			return;
		}

		setIsLoading(true);
		
		try {
			const { data } = await props.client.query({
				query: QUERY_BADGE_ASSIGNMENTS,
				variables: {
					companyId: companyId,
					nextToken: nextToken
				},
				fetchPolicy: 'network-only'
			});

			const formData = await fetchModernFormQuestions(				
				currentUser,
				'custom',
				'manager_justification_form'
			)
			
			setIsLoading(false);
			
			if (data?.queryBadgeAssignments?.items) {
				// Filter for completed approvals (Approved or Declined)
				const completedItems = data.queryBadgeAssignments.items.filter(
					item => item.approvalStatus === 'Approved' || item.approvalStatus === 'Declined'
				);

				// Transform the data to match the table structure
				const formattedData = completedItems.map(item => ({
					id: item.id,
					recipient: `${item.user?.firstName || ''} ${item.user?.lastName || ''}`,
					recipientId: item.userId,
					costInCurrency: `${item.badge?.snappyGrant?.currencySymbol || '$'}${item.badge?.snappyGrant?.costInCurrency || 0}` || 'N/A',
					badgeName: item.badge?.name || 'Unknown Badge',
					name: item.badge?.snappyGrant?.name || 'N/A',
					createdBy: item.awardedByUser ? `${item.awardedByUser.firstName || ''} ${item.awardedByUser.lastName || ''}`.trim() : (item.awardedBy || 'Unknown'),
					remainingBudget: item.user?.recognitionEvents?.currentBudget || 0,
					status: item.approvalStatus,
					declineReason: item.declineReason,
					created: item.createdAt,
					completed: item.completedAt || item.updatedAt,
					justification: item.justificationFormAnswers ? getJustificationContent(formData, parse(item.justificationFormAnswers)) : undefined,
					rawData: item // Keep the raw data for reference
				}));

				setApprovalStatus(formattedData);
				setNextToken(data.queryBadgeAssignments.nextToken);
			} else {
				message.error('Failed to fetch approval status');
			}
		} catch (error) {
			setIsLoading(false);
			console.error("Error fetching badge assignments:", error);
			message.error('Failed to fetch approval status');
		}
	};

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
	};

	const handleReset = (clearFilters) => {
		clearFilters();
	};

	const columns = [
		{
			title: 'RECIPIENT',
			dataIndex: 'recipient',
			key: 'recipient',
			...getColumnSearchProps(
				'recipient',
				searchInput,
				handleSearch,
				handleReset,
				'Recipient'
			),
		},
		{
			title: 'TOTAL COST',
			dataIndex: 'costInCurrency',
			key: 'costInCurrency',
			render: (costInCurrency) => (
				<span style={{ color: costInCurrency === 0 ? 'inherit' : 'green' }}>
					{costInCurrency ?? 0}
				</span>
			),
		},
		{
			title: 'RECOGNITION',
			dataIndex: 'badgeName',
			key: 'badgeName',
		},
		{
			title: 'SNAPPY REWARD',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'CREATED BY',
			dataIndex: 'createdBy',
			key: 'createdBy',
		},
		{
			title: 'REMAINING BUDGET',
			dataIndex: 'remainingBudget',
			key: 'remainingBudget',
			render: (budget) => (
				<span style={{ color: budget <= 0 ? 'red' : 'green' }}>
					${addCommas(budget)}
				</span>
			),
		},
		{
			title: 'CREATED',
			dataIndex: 'created',
			key: 'created',
			sorter: (a, b) => new Date(a?.created) - new Date(b?.created),
			render: (text) => dayjs(text).format('MM/DD/YYYY'),
		},
		{
			title: 'COMPLETED',
			dataIndex: 'completed',
			key: 'completed',
			render: (text) => text ? dayjs(text).format('MM/DD/YYYY') : 'N/A',
		},
		{
			title: 'JUSTIFICATION',
			dataIndex: 'justification',
			key: 'justification',
			render: (content) => (
				content ? (
					<Button
						size="small"
						type="default"
						className="approve-btn mr-2"
						onClick={() => {
							setSelectedJustification(content);
							setShowJustificationModal(true);
						}}
					>
						View
					</Button>
				) : (
					<Paragraph ellipsis={{ rows: 2 }}>
						No justification provided
					</Paragraph>
				)
			),
		},
		{
			title: 'STATUS',
			dataIndex: 'status',
			key: 'status',
			render: (status) => (
				<Tag
					color={
						status === 'Approved'
							? 'green'
							: status === 'Declined'
								? 'red'
								: 'blue'
					}
				>
					{status}
				</Tag>
			),
		},
		{
			title: 'DECLINE REASON',
			dataIndex: 'declineReason',
			key: 'declineReason',
			render: (declineReason) => (
				declineReason ? (
					<Tooltip placement="top" title={declineReason}>
						<i
							className="icon-message-outline"
							alt=""
							style={{ color: 'var(--silver-chalice)', fontSize: 18 }}
						/>
					</Tooltip>
				) : null
			),
		},
	];

	return (
		<div className="mt-4">
			<div className="page-title">
				<h2 className="page-heading-manage-reward">Approval Status</h2>
			</div>
			<ApprovalRewardsTable
				rowKey={(record) => uuid() + record.id}
				columns={columns}
				isLoading={isLoading}
				approvedData={approvalStatus}
				emptyMessage="You have not approved any recognitions yet."
			/>
			<Modal
				destroyOnClose
				width={420}
				open={showJustificationModal}
				title="Justification Answers"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => setShowJustificationModal(false)}
			>
				<>
					<div className="text-center">
						{
							selectedJustification.map((j, i) => (
								(
									<>
										<span>
											<strong>{j.question + ':'}</strong>
										</span>
										<p>
											{j.answer}
										</p>
									</>
								)
							))
						}
					</div>
				</>
			</Modal>
		</div>
	);
};

export default withApollo(ApprovedContainer);
